import React from 'react';
import { cn } from '../utils/cn';
import { motion } from 'framer-motion';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag' | 'ref'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  glass?: boolean; // Enable glassmorphism (default: true)
  darkBackground?: boolean; // For dark backgrounds (default: false)
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'primary',
    size = 'md',
    loading = false,
    glass = true,
    darkBackground = false,
    className,
    children,
    disabled,
    ...props
  }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden';

    // Glassmorphism variants (for dark backgrounds)
    const glassVariants = {
      primary: darkBackground
        ? 'text-white border border-white/20'
        : 'text-white border border-white/10', // Soft black button
      secondary: darkBackground
        ? 'text-white border border-white/30'
        : 'text-slate-900 border border-slate-200/50',
      outline: darkBackground
        ? 'text-white border border-white/25'
        : 'text-slate-900 border border-slate-900/10',
      text: darkBackground
        ? 'text-white border-transparent'
        : 'text-slate-900 border-transparent',
      ghost: darkBackground
        ? 'text-white/80 border border-white/10'
        : 'text-slate-600 border border-slate-200/30'
    };

    // Advanced Glassmorphism styles (Apple iOS 2025 inspired)
    const glassStyles = {
      primary: darkBackground
        ? {
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
        }
        : {
          // Ultimate Soft Black Glass
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.95) 100%)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          boxShadow: `
              0 20px 40px -12px rgba(0, 0, 0, 0.6),
              0 0 0 1px rgba(255, 255, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2),
              inset 0 20px 20px -10px rgba(255, 255, 255, 0.05)
            `,
        },
      secondary: darkBackground
        ? {
          background: 'rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }
        : {
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
        },
      outline: darkBackground
        ? {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }
        : {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
        },
      text: {},
      ghost: darkBackground
        ? {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)',
          boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.15)',
        }
        : {
          background: 'rgba(0, 0, 0, 0.02)',
          backdropFilter: 'blur(10px) saturate(180%)',
          WebkitBackdropFilter: 'blur(10px) saturate(180%)',
          boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.05)',
        },
    };

    // Traditional variants (fallback when glass=false)
    const traditionalVariants = {
      primary: 'bg-slate-900 hover:bg-slate-800 text-white focus:ring-slate-900',
      secondary: 'bg-white hover:bg-gray-50 text-slate-900 border border-slate-200 focus:ring-slate-900',
      outline: 'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white focus:ring-slate-900',
      text: 'text-slate-900 hover:text-slate-700 focus:ring-slate-900',
      ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 focus:ring-slate-900'
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-xs',
      md: 'px-5 py-2.5 text-sm',
      lg: 'px-6 py-3 text-base',
      xl: 'px-8 py-4 text-lg'
    };

    return (
      <motion.button
        ref={ref as any}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          baseClasses,
          glass ? glassVariants[variant] : traditionalVariants[variant],
          sizes[size],
          className
        )}
        style={glass ? glassStyles[variant] : undefined}
        disabled={disabled || loading}
        {...(props as any)}
      >
        {glass && (
          <>
            {/* Advanced Glass Shine Effect */}
            <div
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                background: 'linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.1) 40%, transparent 60%)',
                transform: 'skewX(-20deg)',
              }}
            />

            {/* Inner Glow */}
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background: darkBackground
                  ? 'radial-gradient(circle at center, rgba(255, 255, 255, 0.15) 0%, transparent 70%)'
                  : 'radial-gradient(circle at top, rgba(255, 255, 255, 0.4) 0%, transparent 60%)',
              }}
            />
          </>
        )}
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        <span className="relative z-10 flex items-center">
          {children}
        </span>
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
